/**
 * Developer onboarding (Phase 1, manual): turn an uploaded .zip / .wasm into a GameMeta the
 * carousel can play immediately. Phase 2 replaces this with the ingestion pipeline (wasm-opt,
 * asset compression, telemetry SDK injection at build time).
 *
 * Accepted bundle layout (all optional except one .wasm):
 *   manifest.json   { "id", "title", "developer", "entry": "game.wasm", "data": "game.data",
 *                     "thumbnail": "thumb.png", "verbs": [...], "accent": "#hex" }
 *   *.wasm          the game (Blitz ABI, see sdk/include/blitz.h)
 *   *.data          optional asset bundle delivered through blitz_alloc/blitz_on_data
 *   *.png|jpg|svg   optional thumbnail
 *   index.html      a WEB CARD — the page runs in the sandboxed iframe (see WebCardHost)
 *
 * A bare .html file is also accepted and becomes a web card directly.
 */
import { unzipSync } from 'fflate';
import type { GameMeta } from './protocol';

export interface ImportResult {
  game: GameMeta;
  notes: string[];
  bytes: number;
}

interface Manifest {
  id?: string;
  title?: string;
  developer?: string;
  entry?: string;
  data?: string;
  thumbnail?: string;
  verbs?: string[];
  accent?: string;
  description?: string;
}

const WASM_MAGIC = [0x00, 0x61, 0x73, 0x6d];

/** Distinguishes uploads made in the same millisecond, which Date.now() alone cannot. */
let uploadSeq = 0;

/** Refuse anything that would blow the per-card budget before it reaches a slot. */
const MAX_HTML_BYTES = 2 * 1024 * 1024;

/**
 * Warn about things the sandbox origin's CSP will kill at runtime. These are NOT the security
 * boundary — the sandbox attribute and the CSP are — but a page whose external script is
 * silently blocked just renders blank, and a blank card is indistinguishable from a broken
 * platform. Better to say so at upload time.
 */
function cspWarnings(html: string): string[] {
  const out: string[] = [];
  if (/<script[^>]+src\s*=/i.test(html)) out.push('external <script src> will be blocked by the sandbox CSP — inline it');
  if (/<link[^>]+stylesheet/i.test(html)) out.push('external stylesheet will be blocked — inline it in a <style> tag');
  if (/(fetch|XMLHttpRequest|WebSocket|EventSource)\s*\(/.test(html)) out.push('network calls are blocked (connect-src none)');
  if (/<img[^>]+src\s*=\s*["']https?:/i.test(html)) out.push('remote images are blocked — use a data: URI');
  if (/@import\s+url|fonts\.googleapis/i.test(html)) out.push('remote fonts are blocked — use a system font stack');
  if (/<form/i.test(html)) out.push('form submission is blocked (form-action none)');
  return out;
}

function htmlCard(bytes: Uint8Array, name: string, stamp: string, notes: string[]): ImportResult {
  if (bytes.length > MAX_HTML_BYTES) {
    throw new Error(`${(bytes.length / 1048576).toFixed(1)}MB exceeds the ${MAX_HTML_BYTES / 1048576}MB per-card limit`);
  }
  const html = new TextDecoder().decode(bytes);
  notes.push(...cspWarnings(html));
  const base = slug(name.replace(/\.html?$/i, '')) || 'page';
  return {
    bytes: bytes.length,
    notes,
    game: {
      id: `local-${base}-${stamp}`,
      kind: 'web',
      title: (html.match(/<title[^>]*>([^<]{1,80})</i)?.[1] ?? base).trim(),
      developer: 'You (local upload)',
      wasm_url: '',
      // A blob: URL in a sandbox="allow-scripts" frame gets an opaque origin, so the page
      // cannot reach this document even though the blob was created here.
      page_url: blobUrl(bytes, 'text/html'),
      data_url: null,
      thumbnail_url: DEFAULT_THUMB,
      verbs: ['web', 'uploaded'],
      accent: '#a78bfa',
      local: true,
    },
  };
}

function isWasm(bytes: Uint8Array): boolean {
  return bytes.length > 8 && WASM_MAGIC.every((b, i) => bytes[i] === b);
}

function slug(s: string): string {
  return s
    .toLowerCase()
    .replace(/\.[a-z0-9]+$/, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'game';
}

function blobUrl(bytes: Uint8Array, type: string): string {
  return URL.createObjectURL(new Blob([bytes as BlobPart], { type }));
}

const DEFAULT_THUMB =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" rx="22" fill="#121826"/><path d="M30 30h40v40H30z" fill="none" stroke="#5eead4" stroke-width="6" stroke-dasharray="10 8"/></svg>',
  );

export async function importGameFile(file: File): Promise<ImportResult> {
  const notes: string[] = [];
  const raw = new Uint8Array(await file.arrayBuffer());
  const stamp = `${Date.now().toString(36)}${(uploadSeq++).toString(36)}`;

  if (/\.html?$/i.test(file.name) || file.type === 'text/html') {
    return htmlCard(raw, file.name, stamp, notes);
  }

  if (isWasm(raw) || file.name.endsWith('.wasm')) {
    if (!isWasm(raw)) throw new Error('not a WebAssembly binary (bad magic)');
    const id = `local-${slug(file.name)}-${stamp}`;
    return {
      bytes: raw.length,
      notes,
      game: {
        id,
        title: file.name.replace(/\.wasm$/, ''),
        developer: 'You (local upload)',
        wasm_url: blobUrl(raw, 'application/wasm'),
        data_url: null,
        thumbnail_url: DEFAULT_THUMB,
        verbs: ['unlabelled'],
        accent: '#5eead4',
        local: true,
      },
    };
  }

  let entries: Record<string, Uint8Array>;
  try {
    entries = unzipSync(raw);
  } catch (err) {
    throw new Error(`could not unzip ${file.name}: ${err instanceof Error ? err.message : String(err)}`);
  }
  const names = Object.keys(entries).filter((n) => !n.endsWith('/') && !n.startsWith('__MACOSX/'));
  const find = (pred: (n: string) => boolean) => names.find(pred);

  let manifest: Manifest = {};
  const manifestName = find((n) => n.toLowerCase().endsWith('manifest.json'));
  if (manifestName) {
    try {
      manifest = JSON.parse(new TextDecoder().decode(entries[manifestName])) as Manifest;
    } catch {
      notes.push('manifest.json is not valid JSON — ignored');
    }
  }

  const wasmName =
    (manifest.entry && find((n) => n.endsWith(manifest.entry!))) ||
    find((n) => n.endsWith('.wasm') && isWasm(entries[n]));

  // No wasm but an index.html: that is a web card bundle, not a broken game bundle.
  if (!wasmName) {
    const indexName = find((n) => n.toLowerCase().endsWith('index.html'));
    if (indexName) {
      notes.push('bundled as a web card from index.html; only that file is used, so inline any assets');
      return htmlCard(entries[indexName], manifest.id ?? file.name, stamp, notes);
    }
    throw new Error('no .wasm and no index.html found in the bundle');
  }

  const dataName = (manifest.data && find((n) => n.endsWith(manifest.data!))) || find((n) => n.endsWith('.data'));
  const thumbName =
    (manifest.thumbnail && find((n) => n.endsWith(manifest.thumbnail!))) ||
    find((n) => /\.(png|jpe?g|svg|webp)$/i.test(n));
  if (find((n) => n.toLowerCase().endsWith('index.html'))) {
    notes.push('bundle has both a .wasm and an index.html — running the .wasm via the Blitz ABI');
  }

  const thumbType = thumbName?.endsWith('.svg') ? 'image/svg+xml' : thumbName?.endsWith('.png') ? 'image/png' : 'image/jpeg';
  const base = manifest.id ?? slug(wasmName.split('/').pop() ?? file.name);
  const game: GameMeta = {
    id: `local-${base}-${stamp}`,
    title: manifest.title ?? base,
    developer: manifest.developer ?? 'You (local upload)',
    wasm_url: blobUrl(entries[wasmName], 'application/wasm'),
    data_url: dataName ? blobUrl(entries[dataName], 'application/octet-stream') : null,
    thumbnail_url: thumbName ? blobUrl(entries[thumbName], thumbType) : DEFAULT_THUMB,
    verbs: manifest.verbs ?? ['unlabelled'],
    accent: manifest.accent ?? '#5eead4',
    description: manifest.description,
    local: true,
  };
  return { game, notes, bytes: entries[wasmName].length };
}
